import { AlpacaOrder } from '../reducers/positionSlice';
import { getDateString } from './utils';

export interface BuyLot {
    price: number;
    available_qty: number;
    date: string;
}

export interface TradeHistoryEntry {
    price: number;
    quantity: number;
    side: 'buy' | 'sell';
    available_qty: number;
    date: string;
    profitLoss?: number;
}

export interface RealizedPnLResult {
    tradeHistory: Record<string, TradeHistoryEntry[]>;
    profitLoss: number;
}

/**
 * FIFO realized P/L. Processes all orders chronologically so earlier buys
 * cost-basis later sells, but only counts P/L for sells within the period.
 */
export const calculateRealizedPnL = (
    orders: AlpacaOrder[],
    periodStartDate: string
): RealizedPnLResult => {
    const periodStart = new Date(periodStartDate).getTime();
    let periodRealizedPnL = 0;
    const buyLots: Record<string, BuyLot[]> = {};
    const tradeHistory: Record<string, TradeHistoryEntry[]> = {};

    const sortedOrders = [...orders].sort(
        (a, b) => new Date(a.transaction_time).getTime() - new Date(b.transaction_time).getTime()
    );

    for (const order of sortedOrders) {
        const { symbol, price: filledAvgPrice, qty, side, transaction_time: date } = order;
        const quantity = parseFloat(qty);
        const price = parseFloat(filledAvgPrice);

        if (!Number.isFinite(quantity) || !Number.isFinite(price) || quantity <= 0) {
            continue;
        }

        if (!buyLots[symbol]) {
            buyLots[symbol] = [];
        }
        if (!tradeHistory[symbol]) {
            tradeHistory[symbol] = [];
        }

        if (side === 'buy') {
            buyLots[symbol].push({ price, available_qty: quantity, date });
            tradeHistory[symbol].push({
                price,
                quantity,
                side: 'buy',
                date,
                available_qty: quantity,
            });
            continue;
        }

        if (side !== 'sell') {
            continue;
        }

        let remainingQty = quantity;
        let totalSellProfitLoss = 0;

        while (remainingQty > 0 && buyLots[symbol].length > 0) {
            const lot = buyLots[symbol][0];

            if (lot.available_qty <= 0) {
                buyLots[symbol].shift();
                continue;
            }

            const qtyToSell = Math.min(remainingQty, lot.available_qty);
            totalSellProfitLoss += (price - lot.price) * qtyToSell;
            remainingQty -= qtyToSell;
            lot.available_qty -= qtyToSell;

            if (lot.available_qty <= 0) {
                buyLots[symbol].shift();
            }
        }

        const sellInPeriod = new Date(date).getTime() >= periodStart;
        if (sellInPeriod) {
            periodRealizedPnL += totalSellProfitLoss;
            tradeHistory[symbol].push({
                price,
                quantity,
                side: 'sell',
                date,
                available_qty: quantity,
                profitLoss: parseFloat(totalSellProfitLoss.toFixed(2)),
            });
        }
    }

    return {
        tradeHistory,
        profitLoss: parseFloat(periodRealizedPnL.toFixed(2)),
    };
};

export const getOrdersHistoryFetchDate = (): string => {
    const date = new Date();
    date.setFullYear(date.getFullYear() - 2);
    return `${getDateString(date)}T06:30:00Z`;
};

export const filterOrdersByPeriod = (orders: AlpacaOrder[], periodStartDate: string): AlpacaOrder[] => {
    const periodStart = new Date(periodStartDate).getTime();
    return orders.filter((order) => new Date(order.transaction_time).getTime() >= periodStart);
};
